import { Dialog } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus, useMutation } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useState } from "react";
import { CommitSummaryAndDetails } from "~/areas/projects/components/VersionControl/CommitDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
interface ProjectTemplateCommitDialogProps<TCommand> {
    open: boolean;
    gitRef: string;
    onClose: () => void;
    onCommit: (command: TCommand, gitRef: string | undefined) => Promise<void>;
    getCommandToCommit: () => TCommand;
    defaultCommitMessage?: string;
    entityName?: "project template" | "deployment process" | "parameter set";
}
export function ProjectTemplateCommitDialog<TCommand extends {
    ChangeDescription?: string;
}>({ open, gitRef, onClose, onCommit, getCommandToCommit, defaultCommitMessage = "Update project template", entityName = "project template", }: ProjectTemplateCommitDialogProps<TCommand>) {
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>({ summary: "", details: "" });
    const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState(false);
    useEffect(() => {
        if (open) {
            setCommitMessage({ summary: "", details: "" });
            setHasSaveBeenAttempted(false);
        }
    }, [open]);
    const { execute: saveChanges } = useMutation({
        name: `Save project template ${entityName}`,
        action: async (repository: Repository, commitMessage: CommitMessageWithDetails) => {
            const commandToCommit = getCommandToCommit();
            const updatedCommand: TCommand = {
                ...commandToCommit,
                ChangeDescription: getFormattedCommitMessage(commitMessage, defaultCommitMessage),
            };
            await onCommit(updatedCommand, gitRef);
        },
        afterComplete: async () => {
            setCommitMessage({ summary: "", details: "" });
            setHasSaveBeenAttempted(false);
            onClose();
        },
    });
    const { isInProgress: busy, errors } = useAggregateAPIOperationStatus();
    const shouldShowErrors = errors && hasSaveBeenAttempted && !busy;
    const handleSave = async () => {
        setHasSaveBeenAttempted(true);
        await saveChanges(commitMessage);
        return true;
    };
    return (<Dialog open={open}>
            <SaveDialogLayout title="Commit changes" onSaveClick={handleSave} onCancelClick={onClose} busy={busy} errors={shouldShowErrors ? errors : undefined} savePermission={{ permission: Permission.PlatformHubEdit }} saveButtonLabel="Commit" busyButtonLabel="Committing...">
                <CommitSummaryAndDetails onCommitMessageChanged={setCommitMessage} defaultSummary={defaultCommitMessage} commitMessage={commitMessage} commitMessageAccessibleName={`Commit summary for ${entityName}`} commitDetailsAccessibleName={`Commit details for ${entityName}`}/>
                <p>
                    Octopus will commit the changes to the branch <code>{gitRef}</code>
                </p>
            </SaveDialogLayout>
        </Dialog>);
}
